import React from 'react';
import { motion } from 'framer-motion';
import { 
  Monitor, 
  FileCode, 
  RefreshCw, 
  Database, 
  Shield, 
  Clock, 
  Activity, 
  UserPlus
} from 'lucide-react';
import Container from '../../layout/Container';
import SectionHeading from '../../common/SectionHeading';
import Card from '../../common/Card';
import Button from '../../common/Button';
import { useNavigate } from 'react-router-dom';

const supportTypes = [
  {
    icon: Monitor,
    title: 'Hardware Support',
    description: 'Maintenance and repair services for servers, workstations, printers, and network devices.',
  },
  {
    icon: FileCode,
    title: 'Software Support',
    description: 'Installation, configuration, and troubleshooting of operating systems and business applications.',
  },
  {
    icon: RefreshCw,
    title: 'Preventive Maintenance',
    description: 'Regular check-ups and optimization to prevent system failures and extend hardware life.',
  },
  {
    icon: Database,
    title: 'Data Management',
    description: 'Backup solutions, data recovery, and storage optimization for data integrity and security.',
  },
  {
    icon: Shield,
    title: 'Security Services',
    description: 'Implementation of security measures to protect your systems from threats and vulnerabilities.',
  },
  {
    icon: Clock,
    title: 'Emergency Response',
    description: 'Rapid support for critical issues with minimal response time to reduce downtime.',
  },
  {
    icon: Activity,
    title: 'Performance Monitoring',
    description: 'Continuous monitoring of system performance with alerts for potential issues.',
  },
  {
    icon: UserPlus,
    title: 'User Support',
    description: 'Technical assistance for end-users with hardware and software-related queries.',
  },
];

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const SupportTypes = () => {
  const navigate = useNavigate();

  return (
    <section className="py-20 bg-gray-50">
      <Container>
        <SectionHeading
          title="Types of Support We Offer"
          subtitle="Comprehensive IT support services tailored to your business needs."
          center
        />
        
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12"
        >
          {supportTypes.map((support, index) => (
            <motion.div key={index} variants={item}>
              <Card
                icon={support.icon}
                title={support.title}
                description={support.description}
                hover
                className="h-full"
              />
            </motion.div>
          ))}
        </motion.div>
        
        <div className="mt-8 bg-blue-600 text-white p-8 rounded-lg">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-2xl font-bold mb-4">Custom Support Plans</h3>
            <p className="text-blue-100 mb-6">
              We understand that every business has unique IT support requirements. Our team can create 
              customized support plans that address your specific needs and align with your budget constraints.
            </p>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Button
                onClick={() => navigate('/contact')}
                className="bg-darkBlue text-white font-semibold px-6 py-3 rounded-md"
              >
                Contact us to discuss your custom support needs
              </Button>
            </motion.div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default SupportTypes;